import { CONSOLE_HEADER_RESIZE_EVENT } from 'src/commons/constants';

export const A3C_CONTAINER_ID = 'a3c-embedded-chat-container';
export const DEFAULT_PANEL_WIDTH = 480;
export const NAV_WIDTH = 64;

/**
 * Dispatches a window resize event so page owners can reflow content.
 */
export function dispatchResizeEvent(): void {
  window.dispatchEvent(new Event('resize'));
}

/**
 * Content area selectors in priority order.
 * - .app-and-pivot-wrapper: ChromeUI-managed layout (dev page)
 * - #sc-content-container: BTSW layout (live ads console pages)
 */
const CONTENT_AREA_SELECTORS = [
  '.app-and-pivot-wrapper',
  '#sc-content-container',
];

/**
 * Finds the main content area element, trying each known selector.
 */
function findContentArea(): HTMLElement | null {
  for (const selector of CONTENT_AREA_SELECTORS) {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) return el;
  }
  return null;
}

/**
 * Sets the content area margin and width to account for the nav bar and chat panel.
 * Standard mode: margin = NAV_WIDTH + panelWidth
 * Focus mode: margin = panelWidth
 */
export function adjustContentMargin(
  panelWidth: number,
  isFocusMode: boolean,
): void {
  const contentArea = findContentArea();
  if (!contentArea) return;

  const margin = isFocusMode ? panelWidth : NAV_WIDTH + panelWidth;
  contentArea.style.marginInlineStart = `${margin}px`;
  contentArea.style.width = `calc(100% - ${margin}px)`;
}

/**
 * Listens for header resize events and keeps the container's top
 * position in sync so it never sits behind the fixed page header.
 */
export function attachHeaderResizeListener(container: HTMLElement): void {
  document.addEventListener(CONSOLE_HEADER_RESIZE_EVENT, ((e: Event) => {
    const headerHeight = (e as CustomEvent).detail?.headerHeight;
    if (headerHeight != null) {
      container.style.top = `${headerHeight}px`;
    }
  }) as EventListener);
}

/**
 * Creates the fixed-position container div for the embedded chat panel.
 * Returns the existing element if it's already in the DOM, updating its
 * left position to reflect the current mode.
 */
export function createEmbeddedChatContainer(isFocusMode: boolean): HTMLElement {
  const existing = document.getElementById(A3C_CONTAINER_ID);
  if (existing) {
    existing.style.left = isFocusMode ? '0' : `${NAV_WIDTH}px`;
    return existing;
  }

  const container = document.createElement('div');
  container.id = A3C_CONTAINER_ID;
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.bottom = '0';
  container.style.left = isFocusMode ? '0' : `${NAV_WIDTH}px`;
  container.style.zIndex = '299';
  container.style.width = `${DEFAULT_PANEL_WIDTH}px`;
  container.style.display = 'none';

  document.body.appendChild(container);
  attachHeaderResizeListener(container);
  return container;
}

/**
 * Shows or hides the embedded chat container and adjusts the content margin.
 */
export function setEmbeddedChatVisible(
  visible: boolean,
  panelWidth: number = DEFAULT_PANEL_WIDTH,
  isFocusMode: boolean = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    container.style.display = visible ? 'flex' : 'none';
  }

  if (visible) {
    adjustContentMargin(panelWidth, isFocusMode);
  } else {
    // Reset margin: Standard = NAV_WIDTH, Focus = 0
    adjustContentMargin(0, isFocusMode);
  }

  dispatchResizeEvent();
}

/**
 * Handles a resize of the embedded chat panel by updating the container
 * width and adjusting the content margin.
 */
export function handleEmbeddedChatResize(
  width: number,
  isFocusMode: boolean = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    container.style.width = `${width}px`;
  }

  adjustContentMargin(width, isFocusMode);
  dispatchResizeEvent();
}

/**
 * Module-level focus mode state so callbacks always use the current value.
 */
let currentFocusMode = false;

/**
 * Updates the stored focus mode state. Call this whenever the mode changes.
 */
export function setCurrentFocusMode(isFocusMode: boolean): void {
  currentFocusMode = isFocusMode;
}

interface InitA3CEmbeddedConfigOptions {
  closable: boolean;
  onStateChange?: (isOpen: boolean) => void;
  selectedAgentIdentifier?: string;
}

/**
 * Initializes window.A3CConfig with embedded mode settings.
 * Merges with any existing config (preserving page owner's panelSizes).
 * Callbacks reference the module-level focus mode so they never go stale.
 */
export function initA3CEmbeddedConfig({
  closable,
  onStateChange,
  selectedAgentIdentifier,
}: InitA3CEmbeddedConfigOptions): void {
  const existing = (window as any).A3CConfig || {};
  (window as any).A3CConfig = {
    ...existing,
    containerId: A3C_CONTAINER_ID,
    closable,
    selectedAgentIdentifier,
    onClose: () => {
      setEmbeddedChatVisible(false, DEFAULT_PANEL_WIDTH, currentFocusMode);
      onStateChange?.(false);
    },
    onResize: (width: number) => {
      handleEmbeddedChatResize(width, currentFocusMode);
    },
  };
}
